import React, { useEffect, useState } from "react";
import "./coverPhotos.css";
import { reduceMotionSetting } from "@/hooks/useReducedMotion";

const SECONDS = 6;

/**
 * Cover photographs behind a dark band (owner, 2026-10-04). They take turns
 * every six seconds, fading in and drifting slowly while on show, under a
 * night-blue veil that keeps the white title readable. Each cover is three
 * files cut for its screen, in `base`:
 * <id>-desktop.webp from 1024px, <id>-tablet.webp from 768px and
 * <id>-phone.webp below.
 *
 * Only three covers are drawn at a time: the one on show, the one before it
 * (still under it while the new one fades in) and the next one (loading
 * unseen), so a visitor downloads three photographs, not ten. A visitor who
 * asked for less motion keeps the first cover, with no timer. Decorative:
 * the band's own words say what the page is. `onIndex` hears which cover is
 * on show (the apps' Accueil labels its place); `veil={false}` leaves the
 * night-blue veil to the page (the apps draw their own gradient).
 */
export default function CoverPhotos({ covers, base, testId = "cover-photos", onIndex, veil = true }) {
  const [index, setIndex] = useState(0);
  const count = covers?.length || 0;

  useEffect(() => {
    if (count < 2) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || reduceMotionSetting()) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), SECONDS * 1000);
    return () => clearInterval(timer);
  }, [count]);

  useEffect(() => { if (count) onIndex?.(index); }, [index, count, onIndex]);

  if (!count) return null;
  const at = (step) => (index + step + count) % count;
  // With two covers, the other one is only ever the next.
  const roles = new Map();
  if (count > 1) roles.set(at(1), "next");
  if (count > 2) roles.set(at(-1), "previous");
  roles.set(index, "current");

  return (
    <div className="tk-covers" aria-hidden="true" data-testid={testId}>
      {[...roles].map(([slot, role]) => {
        const { id } = covers[slot];
        return (
          <picture key={id} className="tk-cover" data-role={role} data-cover={id}>
            <source media="(min-width: 1024px)" srcSet={`${base}/${id}-desktop.webp`} />
            <source media="(min-width: 768px)" srcSet={`${base}/${id}-tablet.webp`} />
            <img src={`${base}/${id}-phone.webp`} alt="" decoding="async" fetchPriority={slot === 0 ? "high" : "low"} />
          </picture>
        );
      })}
      {veil ? <span className="tk-covers__veil" /> : null}
    </div>
  );
}
